import type { DraftLineItem, Resolution } from "../types";

/** Key of `ComputeReturnFeesResponse.byResolution` for a widget resolution. */
export type FeeResolutionKey =
  | "refund"
  | "discount"
  | "exchange"
  | "exchangeCredit";

export const FEE_KEY_BY_RESOLUTION: Record<Resolution, FeeResolutionKey> = {
  refund: "refund",
  discount: "discount",
  exchange: "exchange",
  "exchange-credit": "exchangeCredit",
};

/**
 * `compute-return-fees` returns currency units (6.2), because the fee comes from
 * the carrier pricing amount. Everything else in the widget is in cents.
 */
export function feeToCents(fee: number): number {
  return Math.round(fee * 100);
}

/**
 * The return fee is charged once per return request, so only the first article of a
 * given resolution carries the deduction — same rule as the legacy
 * `hasApplicableReturnFeesResolution` in `use-has-to-pay-return-cost.ts`.
 */
export function isFirstFeeBearingArticle(
  lineItems: DraftLineItem[],
  currentLineItemId: string,
  resolution: Resolution,
): boolean {
  return !lineItems.some(
    (li) => li.lineItemId !== currentLineItemId && li.resolution === resolution,
  );
}

export type PaidReturnMethod = "refund" | "discount";

const METHOD_BY_RESOLUTION: Record<Resolution, PaidReturnMethod | null> = {
  refund: "refund",
  discount: "discount",
  "exchange-credit": "discount",
  exchange: null,
};

const METHOD_PRIORITY: PaidReturnMethod[] = ["refund", "discount"];

/**
 * Bucket the once-per-request fee is deducted from. The backend defaults to `refund`
 * when the portal sends nothing, which charges an empty bucket on a credit-only basket.
 */
export function resolvePaidReturnMethod(
  lineItems: DraftLineItem[],
): PaidReturnMethod | null {
  return (
    METHOD_PRIORITY.find((method) =>
      lineItems.some(
        (li) => li.resolution && METHOD_BY_RESOLUTION[li.resolution] === method,
      ),
    ) ?? null
  );
}
